export const SUPPORTED_CURRENCIES = ['CHF', 'EUR', 'USD', 'GBP'];
export const DEFAULT_CURRENCY = 'CHF';

// Digit grouping/decimal separators are ALWAYS the app's Swiss house style
// (1'234.50), independent of the currency being labeled - mixing per-currency
// digit conventions (e.g. de-DE comma decimals for EUR) inside one table
// column is unreadable. Only the currency code/symbol varies.
const NUMBER_LOCALE = 'de-CH';

let activeCurrency = DEFAULT_CURRENCY;

export function setActiveCurrency(code) {
  if (SUPPORTED_CURRENCIES.includes(code)) {
    activeCurrency = code;
  }
}

export function getActiveCurrency() {
  return activeCurrency;
}

/**
 * Formats a monetary value.
 * - Explicit `recordCurrency` = the document/converted currency. Pass it for
 *   document amounts (invoice totals in their FX currency) AND for converted
 *   amounts whose currency is mathematically fixed (pass 'CHF' for
 *   CHF-normalized sums, CHF equivalents, FX-rate previews).
 * - No argument = the global display currency (GeneralSettings). Use ONLY for
 *   legacy/unconverted amounts that carry no per-record currency.
 * Digits always use the Swiss house locale; only the code/symbol differs.
 * @param {number|string|null|undefined} value
 * @param {string} [recordCurrency] ISO code of the document/converted currency
 * @returns {string}
 */
export function formatCurrency(value, recordCurrency) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) {
    return '-';
  }
  const currency = recordCurrency || activeCurrency;
  try {
    return new Intl.NumberFormat(NUMBER_LOCALE, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency} ${num.toFixed(2)}`;
  }
}

export function formatNumber(value, maxFractionDigits = 2) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) {
    return '-';
  }
  return new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: maxFractionDigits }).format(num);
}

/** Ein reiner Kalendertag, JJJJ-MM-TT, ohne Uhrzeit. */
const NUR_TAG = /^\d{4}-\d{2}-\d{2}$/;

export function formatDate(value) {
  if (!value) {
    return '-';
  }
  /**
   * Ein Kalendertag wird OHNE Zeitzone gelesen.
   *
   * new Date('2031-12-31') ist nach der Norm UTC-Mitternacht. Intl formatiert
   * danach in der Zeitzone des Betrachters: in Mitteleuropa ist das 01 Uhr am
   * 31., westlich von Greenwich aber der 30. Dezember. Aus einem Buchungsdatum
   * wuerde je nach Standort ein anderer Tag - und im Fall des 1. Januar ein
   * anderes Geschaeftsjahr.
   *
   * Mit den lokalen Bestandteilen gebaut, bleibt der Tag der Tag. Dieselbe
   * Ueberlegung wie bei alsKalendertag() im Backend, nur von der anderen Seite:
   * ein DATE hat keine Zeitzone, also darf keine hineingerechnet werden.
   */
  if (typeof value === 'string' && NUR_TAG.test(value)) {
    const [j, m, t] = value.split('-').map(Number);
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(j, m - 1, t));
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

export function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

const MINUTE_S = 60;
const HOUR_S = 3600;
const DAY_S = 86_400;
const WEEK_S = 604_800;
const MONTH_S = 2_629_800; // average Gregorian month (365.25 d / 12)

/**
 * Relative time for notification timestamps ("vor 5 Minuten", "5 minutes ago").
 * Pure: locale comes from the caller (active i18n language); `now` is injectable
 * for tests. `numeric: 'auto'` turns 0 seconds into "jetzt" / "now".
 * @param {string|Date} iso
 * @param {string} locale
 * @param {number} [now] epoch ms, defaults to Date.now()
 * @returns {string}
 */
export function timeAgo(iso, locale, now = Date.now()) {
  if (!iso) {
    return '-';
  }
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return String(iso);
  }
  const diffSeconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffSeconds < MINUTE_S) {
    return rtf.format(0, 'second');
  }
  if (diffSeconds < HOUR_S) {
    return rtf.format(-Math.floor(diffSeconds / MINUTE_S), 'minute');
  }
  if (diffSeconds < DAY_S) {
    return rtf.format(-Math.floor(diffSeconds / HOUR_S), 'hour');
  }
  if (diffSeconds < WEEK_S) {
    return rtf.format(-Math.floor(diffSeconds / DAY_S), 'day');
  }
  if (diffSeconds < MONTH_S) {
    return rtf.format(-Math.floor(diffSeconds / WEEK_S), 'week');
  }
  return rtf.format(-Math.floor(diffSeconds / MONTH_S), 'month');
}

export function initials(firstName, lastName) {
  const f = (firstName || '').trim().charAt(0).toUpperCase();
  const l = (lastName || '').trim().charAt(0).toUpperCase();
  return `${f}${l}` || '·';
}

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function addDaysIso(days) {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}
